/**
 * What this document is MADE OF — its variants and its sections.
 *
 * A batch manufacturing record is not one form. It is a work-order header, a
 * dispensing sheet, and then whichever process sections the product's route
 * calls for. This tab is where that is said: which master templates exist and
 * when each applies, and which sections each master is composed of — some
 * always present, some produced one-per-row from a source the record points
 * at.
 *
 * **It authors the DECLARATION, not the document.** A section's own fields and
 * body are edited through the ordinary Fields and Body tabs with that template
 * selected, because a section IS an ordinary template: it has fields with
 * types and tolerances, steps with signatures, and its own revision history.
 * Giving sections their own editor would have been a second of everything to
 * keep in step with the first.
 *
 * The one rule worth stating on the screen, because it surprises people: the
 * section list is resolved ONCE, when a document is first opened and the item
 * identified, and frozen onto that document. Editing a route afterwards
 * changes the next batch, never one already running.
 */
import { useMemo, useState } from 'react'

import type { FormModel, FormTemplate, SectionRow } from './types'
import { buttonStyle, inputStyle } from './styles'

const SLUG = /[^A-Za-z0-9_]+/g

function slug(value: string): string {
  return value.replace(SLUG, '_').replace(/^_+|_+$/g, '').toUpperCase()
    .slice(0, 60)
}

function blankSection(key: string): SectionRow {
  return {
    key, title: '', source: 'static', template: '', for_each: '',
    template_from: '', template_fallback: '', section_no: '',
    repeatable: false, optional: false, page_break: true, binds: null,
  }
}

function blankTemplate(key: string, role: 'master' | 'section'): FormTemplate {
  return {
    key, title: key.replace(/_/g, ' ').toLowerCase()
      .replace(/(^|\s)\S/g, (c) => c.toUpperCase()),
    revision: 1, role,
    ...(role === 'section' ? { lock_after_signoff: true } : {}),
    applies_when: '', sections: [],
    body: { type: 'doc', content: [] }, fields: [], steps: [],
  }
}

export default function SectionsTab({ model, mutate, onEdit }: {
  model: FormModel
  mutate: (fn: (m: FormModel) => void) => void
  /** Open a template in the Body tab — the natural next click after adding one. */
  onEdit: (templateKey: string) => void
}) {
  const templates = model.form.templates || []
  const masters = templates.filter((t) => (t.role || 'master') === 'master')
  const sections = templates.filter((t) => t.role === 'section')
  const [openMaster, setOpenMaster] = useState(masters[0]?.key || '')

  /** Every field of the master that could be looped over for a `for_each`. */
  const rowSources = useMemo(() => {
    const master = masters.find((m) => m.key === openMaster) || masters[0]
    return (master?.fields || [])
      .filter((f) => f.kind === 'repeat'
        || (f.binding as { returns?: string } | undefined)?.returns === 'rows')
      .map((f) => ({ key: f.key, title: f.title || f.key }))
  }, [masters, openMaster])

  const patchTemplate = (key: string, fn: (t: FormTemplate) => void) =>
    mutate((m) => {
      const found = (m.form.templates || []).find((t) => t.key === key)
      if (found) fn(found)
    })

  const addTemplate = (role: 'master' | 'section') => {
    const base = role === 'master' ? 'VARIANT' : 'SECTION'
    let n = 1
    let key = `${base}_${n}`
    while (templates.some((t) => t.key === key)) { n += 1; key = `${base}_${n}` }
    mutate((m) => { m.form.templates.push(blankTemplate(key, role)) })
    onEdit(key)
  }

  const removeTemplate = (key: string) => mutate((m) => {
    m.form.templates = m.form.templates.filter((t) => t.key !== key)
    // …and every section declaration that pointed at it, or a master would be
    // left laying out a template that no longer exists — which the engine
    // refuses at open, on the operator's screen rather than here.
    for (const t of m.form.templates) {
      t.sections = (t.sections || []).filter((s) => s.template !== key)
    }
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18,
      maxWidth: 860 }}>

      <Explain />

      {/* ── variants ─────────────────────────────────────────────────── */}
      <section>
        <Heading
          title="Which template a record is filled on"
          hint={masters.length > 1
            ? 'The first master whose rule matches wins. One with no rule is '
              + 'the default.'
            : 'One master, so every record is filled on it. Add another to '
              + 'make the template depend on the record.'}
        />

        {masters.length > 1 && (
          <Selector model={model} mutate={mutate} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6,
          marginTop: 8 }}>
          {masters.map((m) => (
            <div key={m.key} style={rowStyle}>
              <input
                value={m.title}
                placeholder="Tablet BMR"
                onChange={(e) => patchTemplate(m.key, (t) => {
                  t.title = e.target.value
                })}
                style={{ ...inputStyle, flex: '0 0 190px' }}
              />
              <code style={codeStyle}>{m.key}</code>
              {masters.length > 1 && (
                <input
                  value={m.applies_when || ''}
                  placeholder="{sel.ITEM_TYPE} == 'TAB'   (blank = the default)"
                  onChange={(e) => patchTemplate(m.key, (t) => {
                    t.applies_when = e.target.value
                  })}
                  style={{ ...inputStyle, flex: 1, fontFamily: 'monospace',
                    fontSize: '0.8425rem' }}
                />
              )}
              <button type="button" style={buttonStyle(false)}
                onClick={() => setOpenMaster(m.key)}>
                {openMaster === m.key ? 'Sections ↓' : 'Sections'}
              </button>
              <button type="button" style={buttonStyle(false)}
                onClick={() => onEdit(m.key)}>Body</button>
              {masters.length > 1 && (
                <button type="button" style={linkStyle}
                  onClick={() => removeTemplate(m.key)}>Remove</button>
              )}
            </div>
          ))}
        </div>
        <button type="button" style={{ ...buttonStyle(false), marginTop: 8 }}
          onClick={() => addTemplate('master')}>Add a variant</button>
      </section>

      {/* ── the sections of the open master ──────────────────────────── */}
      {masters.filter((m) => m.key === (openMaster || masters[0]?.key))
        .map((master) => (
          <SectionList
            key={master.key}
            master={master}
            sections={sections}
            rowSources={rowSources}
            patch={(fn) => patchTemplate(master.key, fn)}
            onEdit={onEdit}
          />
        ))}

      {/* ── the section templates themselves ─────────────────────────── */}
      <section>
        <Heading
          title="Section templates"
          hint="Each is an ordinary template with its own fields, limits and
                sign-off — edited on the Fields and Body tabs. A section is
                never opened on its own; it is composed into a master."
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sections.map((s) => (
            <div key={s.key} style={rowStyle}>
              <input
                value={s.title}
                placeholder="Granulation"
                onChange={(e) => patchTemplate(s.key, (t) => {
                  t.title = e.target.value
                })}
                style={{ ...inputStyle, flex: '0 0 220px' }}
              />
              <code style={codeStyle}>{s.key}</code>
              <span style={{ flex: 1, fontSize: '0.8425rem',
                color: 'var(--color-text-secondary)' }}>
                {(s.fields || []).length} field
                {(s.fields || []).length === 1 ? '' : 's'}
                {(s.steps || []).length
                  ? ` · ${(s.steps || []).length} step`
                    + `${(s.steps || []).length === 1 ? '' : 's'}`
                  : ' · no sign-off yet'}
              </span>
              <label
                title={'On: once its signatures are complete the section '
                  + 'takes no more changes — correcting it means a new '
                  + 'version. Off: it stays editable, and a changed value '
                  + 'needs a written reason, recorded as an amendment.'}
                style={{ display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: '0.8425rem', whiteSpace: 'nowrap' }}>
                <input type="checkbox"
                  checked={s.lock_after_signoff !== false}
                  onChange={(e) => patchTemplate(s.key, (t) => {
                    t.lock_after_signoff = e.target.checked
                  })} />
                Lock after sign-off
              </label>
              <button type="button" style={buttonStyle(false)}
                onClick={() => onEdit(s.key)}>Edit</button>
              <button type="button" style={linkStyle}
                onClick={() => removeTemplate(s.key)}>Remove</button>
            </div>
          ))}
          {!sections.length && (
            <p style={emptyStyle}>
              None yet. A document with no sections is an ordinary form, which
              is what most of them should be.
            </p>
          )}
        </div>
        <button type="button" style={{ ...buttonStyle(false), marginTop: 8 }}
          onClick={() => addTemplate('section')}>Add a section template</button>
      </section>
    </div>
  )
}

// ── the section list of one master ─────────────────────────────────────────

function SectionList({ master, sections, rowSources, patch, onEdit }: {
  master: FormTemplate
  sections: FormTemplate[]
  rowSources: { key: string; title: string }[]
  patch: (fn: (t: FormTemplate) => void) => void
  onEdit: (key: string) => void
}) {
  const rows = master.sections || []

  const update = (index: number, fn: (s: SectionRow) => void) =>
    patch((t) => {
      t.sections = t.sections || []
      if (t.sections[index]) fn(t.sections[index])
    })

  const add = () => patch((t) => {
    t.sections = t.sections || []
    let n = t.sections.length + 1
    while (t.sections.some((s) => s.key === `SECTION_${n}`)) n += 1
    t.sections.push(blankSection(`SECTION_${n}`))
  })

  const move = (index: number, by: number) => patch((t) => {
    const list = t.sections || []
    const to = index + by
    if (to < 0 || to >= list.length) return
    const [row] = list.splice(index, 1)
    list.splice(to, 0, row)
  })

  return (
    <section>
      <Heading
        title={`What ${master.title || master.key} is made of`}
        hint="In this order. Resolved once, when a document is first opened —
              editing a route afterwards changes the next batch, never one
              already running."
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((row, index) => (
          <div key={row.key} style={{
            border: '1px solid var(--color-border)', borderRadius: 6,
            padding: '10px 12px', display: 'flex', flexDirection: 'column',
            gap: 8,
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center',
              flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '0.8625rem',
                color: 'var(--color-text-secondary)' }}>
                {index + 1}.
              </strong>
              <select
                value={row.source}
                onChange={(e) => update(index, (s) => {
                  s.source = e.target.value as SectionRow['source']
                  // A `for_each` is repeatable by nature: the same process may
                  // legitimately run twice in one route.
                  if (s.source === 'for_each') s.repeatable = true
                })}
                style={{ ...inputStyle, flex: '0 0 210px' }}
              >
                <option value="static">Always present</option>
                <option value="for_each">One per row of…</option>
              </select>

              {row.source === 'static' ? (
                <select
                  value={row.template}
                  onChange={(e) => update(index, (s) => {
                    s.template = e.target.value
                    if (!s.key || s.key.startsWith('SECTION_')) {
                      s.key = slug(e.target.value)
                    }
                  })}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  <option value="">— which section template? —</option>
                  {sections.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.title || s.key}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={row.for_each}
                  onChange={(e) => update(index, (s) => {
                    s.for_each = e.target.value
                  })}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  <option value="">— which rows? —</option>
                  {rowSources.map((f) => (
                    <option key={f.key} value={f.key}>{f.title}</option>
                  ))}
                </select>
              )}

              <span style={{ flex: 1 }} />
              <button type="button" style={linkStyle}
                disabled={index === 0}
                onClick={() => move(index, -1)}>↑</button>
              <button type="button" style={linkStyle}
                disabled={index === rows.length - 1}
                onClick={() => move(index, 1)}>↓</button>
              <button type="button" style={linkStyle}
                onClick={() => patch((t) => {
                  t.sections = (t.sections || [])
                    .filter((_, i) => i !== index)
                })}>Remove</button>
            </div>

            {row.source === 'for_each' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                flexWrap: 'wrap' }}>
                <label style={labelStyle}>
                  Which section, per row
                  <input
                    value={row.template_from}
                    placeholder="{ROUTE.BMR_SECTION}"
                    onChange={(e) => update(index, (s) => {
                      s.template_from = e.target.value
                    })}
                    style={{ ...inputStyle, fontFamily: 'monospace',
                      fontSize: '0.8425rem', minWidth: 220 }}
                  />
                </label>
                <label style={labelStyle}>
                  When there is no such section
                  <select
                    value={row.template_fallback}
                    onChange={(e) => update(index, (s) => {
                      s.template_fallback = e.target.value
                    })}
                    style={{ ...inputStyle, minWidth: 180 }}
                  >
                    {/* Refusing is the default because a section silently
                        missing from a record is worse than one that will not
                        open. It is a poor default for a REAL source, though —
                        most steps of a route have no sheet written for them —
                        so the choice is put next to the expression that picks
                        one rather than left to be discovered on a row that
                        matched nothing. */}
                    <option value="">— refuse the document —</option>
                    {sections.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.title || s.key}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={labelStyle}>
                  Numbered
                  <input
                    value={row.section_no}
                    placeholder="{SEQ_NO}"
                    onChange={(e) => update(index, (s) => {
                      s.section_no = e.target.value
                    })}
                    style={{ ...inputStyle, fontFamily: 'monospace',
                      fontSize: '0.8425rem', width: 130 }}
                  />
                </label>
                <label style={{ ...labelStyle, flexDirection: 'row',
                  alignItems: 'center', gap: 5 }}>
                  <input type="checkbox" checked={row.optional}
                    onChange={(e) => update(index, (s) => {
                      s.optional = e.target.checked
                    })} />
                  May legitimately produce none
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: 14, alignItems: 'center',
              flexWrap: 'wrap' }}>
              {row.source === 'static' && (
                <label style={labelStyle}>
                  Numbered
                  <input
                    value={row.section_no}
                    placeholder="1"
                    onChange={(e) => update(index, (s) => {
                      s.section_no = e.target.value
                    })}
                    style={{ ...inputStyle, width: 90 }}
                  />
                </label>
              )}
              <label style={{ ...labelStyle, flexDirection: 'row',
                alignItems: 'center', gap: 5 }}>
                <input type="checkbox" checked={row.page_break}
                  onChange={(e) => update(index, (s) => {
                    s.page_break = e.target.checked
                  })} />
                Starts on a new page
              </label>
              {row.source === 'static' && row.template && (
                <button type="button" style={linkStyle}
                  onClick={() => onEdit(row.template)}>
                  Edit {row.template}
                </button>
              )}
            </div>
          </div>
        ))}
        {!rows.length && (
          <p style={emptyStyle}>
            No sections, so this master is one ordinary document.
          </p>
        )}
      </div>
      <button type="button" style={{ ...buttonStyle(false), marginTop: 8 }}
        onClick={add}>Add a section</button>
      <p style={{ ...emptyStyle, marginTop: 8 }}>
        Place them in the body with a <strong>section block</strong>. One block
        per declaration; the engine draws as many as the record produces. A
        declaration you never place is appended at the end rather than lost.
      </p>
    </section>
  )
}

// ── the selector ───────────────────────────────────────────────────────────

function Selector({ model, mutate }: {
  model: FormModel
  mutate: (fn: (m: FormModel) => void) => void
}) {
  const sel = (model.form.template_selector || {}) as Record<string, unknown>
  const fill = (sel.fill as { field?: string; column?: string }[]) || []

  const patch = (fn: (s: Record<string, unknown>) => void) => mutate((m) => {
    const next = { ...(m.form.template_selector || {}) } as
      Record<string, unknown>
    fn(next)
    m.form.template_selector = next
  })

  return (
    <div style={{
      border: '1px dashed var(--color-border)', borderRadius: 6,
      padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8,
      marginTop: 6,
    }}>
      <p style={{ ...emptyStyle, margin: 0 }}>
        Look something up about the record first, and each variant's rule tests
        it as <code>{'{sel.COLUMN}'}</code>. For a batch record that is the
        item's type, off the item master.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={labelStyle}>
          Look in
          <input
            value={String(sel.source_object || '')}
            placeholder="ITEM_MASTER"
            onChange={(e) => patch((s) => {
              s.source_object = e.target.value.toUpperCase()
            })}
            style={{ ...inputStyle, minWidth: 170 }}
          />
        </label>
        <label style={labelStyle}>
          Matching its
          <input
            value={String(sel.match_column || '')}
            placeholder="ITEM_CODE"
            onChange={(e) => patch((s) => {
              s.match_column = e.target.value.toUpperCase()
            })}
            style={{ ...inputStyle, minWidth: 150 }}
          />
        </label>
        <label style={labelStyle}>
          against
          <input
            value={String(sel.match_value || '')}
            placeholder="{ctx.ITEM_CODE}"
            onChange={(e) => patch((s) => { s.match_value = e.target.value })}
            style={{ ...inputStyle, minWidth: 170, fontFamily: 'monospace',
              fontSize: '0.8425rem' }}
          />
        </label>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {fill.map((f, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: '0.8425rem',
              color: 'var(--color-text-secondary)' }}>
              {'{sel.'}
            </span>
            <input
              value={f.field || ''}
              placeholder="ITEM_TYPE"
              onChange={(e) => patch((s) => {
                const list = [...((s.fill as typeof fill) || [])]
                list[i] = { ...list[i], field: e.target.value.toUpperCase() }
                s.fill = list
              })}
              style={{ ...inputStyle, width: 170 }}
            />
            <span style={{ fontSize: '0.8425rem',
              color: 'var(--color-text-secondary)' }}>{'}'} ←</span>
            <input
              value={f.column || ''}
              placeholder="ITEM_TYPE"
              onChange={(e) => patch((s) => {
                const list = [...((s.fill as typeof fill) || [])]
                list[i] = { ...list[i], column: e.target.value.toUpperCase() }
                s.fill = list
              })}
              style={{ ...inputStyle, width: 170 }}
            />
            <button type="button" style={linkStyle}
              onClick={() => patch((s) => {
                s.fill = ((s.fill as typeof fill) || [])
                  .filter((_, j) => j !== i)
              })}>Remove</button>
          </div>
        ))}
        <button type="button" style={{ ...buttonStyle(false), width: 'fit-content' }}
          onClick={() => patch((s) => {
            s.fill = [...((s.fill as typeof fill) || []),
              { field: '', column: '' }]
          })}>Read another column</button>
      </div>
    </div>
  )
}

// ── chrome ─────────────────────────────────────────────────────────────────

function Explain() {
  return (
    <p style={{
      fontSize: '0.8825rem', color: 'var(--color-text-secondary)',
      lineHeight: 1.55, margin: 0,
      borderLeft: '3px solid var(--color-border)', paddingLeft: 10,
    }}>
      A composed document is assembled once, when it is first opened and the
      record identified, and the result is frozen onto it. Every later open
      draws the same sections in the same order, at the same revisions —
      so a route revised in the middle of a batch belongs to the next batch,
      and the difference is reported on the document rather than applied to it.
    </p>
  )
}

function Heading({ title, hint }: { title: string; hint: string }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <h3 style={{ fontSize: '0.9825rem', margin: '0 0 2px' }}>{title}</h3>
      <p style={{ ...emptyStyle, margin: 0 }}>{hint}</p>
    </div>
  )
}

const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
}

const codeStyle: React.CSSProperties = {
  fontSize: '0.8125rem', padding: '2px 6px', borderRadius: 4,
  background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
}

const linkStyle: React.CSSProperties = {
  background: 'none', border: 'none', padding: '2px 4px',
  color: 'var(--color-accent)', cursor: 'pointer', fontSize: '0.8425rem',
}

const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.8225rem',
  color: 'var(--color-text-secondary)',
}

const emptyStyle: React.CSSProperties = {
  fontSize: '0.8425rem', color: 'var(--color-text-secondary)', lineHeight: 1.5,
}
